import { Injectable } from '@angular/core';
import { Observable, of, throwError, Subject, from } from 'rxjs';
import { catchError, tap, map, debounceTime, shareReplay, timeout, concatMap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { ToastService } from './toast.service';
import { SessionStore } from '../stores/session.store'
import { environment } from '../../environments/environment';
import { ImageCacheService } from './image-cache.service';
import { NativeLayerService } from './native-layer.service';
import { getLuminaryFormData } from '@ildes/utils/formLuminaryData';

const PROTOCOL = environment.protocol;
const HOST = environment.host;
const PORT = environment.port;

function endpoint(service) {
  return `${PROTOCOL}://${HOST}:${PORT}${service}`;
}

@Injectable({
  providedIn: 'root'
})
export class ApiManagerService {

  cache: any = {};
  showStack = false;

  constructor(
    private http: HttpClient,
    private toast: ToastService,
    private cacheService: ImageCacheService,
    private native: NativeLayerService
  ) { }

  signin(userData, options = { error: true }): Observable<any> {
    return this.http.post(endpoint('/session'), userData, { headers: new HttpHeaders({  timeout: '5000' }), observe: 'response' })
      .pipe(
        map(data => ({
          user: data.body,
          token: data.headers.get('jwt'),
          tokenNative: data.headers.get('jwt-native')
        })),
        catchError((error) => options.error && this.handleError(error))
      )
  }

   signinAdmin(userData, options = { error: true }): Observable<any> {
    return this.http.post(endpoint('/admin/session'), userData, { headers: new HttpHeaders({  timeout: '5000' }), observe: 'response' })
      .pipe(
        map(data => ({
          user: data.body,
          token: data.headers.get('jwt'),
          tokenNative: data.headers.get('jwt-native')
        })),
        catchError((error) => options.error && this.handleError(error))
      )
  }

  updateUser(id, data, options = { error: true }) {
    return this.http.patch(endpoint(`/users/${id}`), data)
      .pipe(
        tap(() => this.toast.show('Usuario modificado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  changePhoto(id, data, options = { error: true }) {
    return this.http.patch(endpoint(`/users/photo/${id}`), data)
      .pipe(
        tap(() => this.toast.show('Foto modificada correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  createUser(data, options = { error: true }) {
    return this.http.post(endpoint('/users'), data)
      .pipe(
        tap(() => this.toast.show('Usuario creado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listUsers(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/users'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getUserDetail(id, options = { error: true }) {
    return this.http.get(endpoint(`/users/${id}`))
      .pipe(
        timeout(5000),
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  createMaterial(data, options = { error: true }) {
    return this.http.post(endpoint('/materials'), data)
      .pipe(
        tap(() => this.toast.show('Material creado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listMaterials(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/materials'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );

    // const normalizedEndpoint = endpoint('/materials');
    // const opts = { params: new HttpParams({ fromString: this.getQPFromJson(params) }) };
    //
    // // if (options.cache && this.cache[normalizedEndpoint]) {
    // //   return this.cache[normalizedEndpoint];
    // // }
    //
    // const observable = this.http.get(normalizedEndpoint, opts)
    //   .pipe(
    //     // shareReplay(1),
    //     catchError((error) => {
    //       // delete this.cache[normalizedEndpoint];
    //       return options.error && this.handleError(error)
    //     })
    //   );
    // // if (options.cache ) {
    // //   this.cache[normalizedEndpoint] = observable;
    // // }
    //
    // return observable

  }

  createProject(data, options = { error: true }) {
    return this.http.post(endpoint('/projects'), data)
      .pipe(
        tap(() => this.toast.show('Proyecto creado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listProjects(params = {}, options = { error: true, cache: false }) {
    const opts = {
        params: new HttpParams({fromString: this.getQPFromJson(params)}),
        headers: new HttpHeaders({ 'cache': `${options.cache}`, timeout: '6000' })
    };

    return this.http.get(endpoint('/projects'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  checkPrefix(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/projects/check-prefix'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listStores(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/stores'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  changeParticipants(id, data, options = { error: true }) {
    return this.http.patch(endpoint(`/projects/${id}/participants`), data)
      .pipe(
        tap(() => this.toast.show('Paricipantes modificados correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getFromCatalog(type, params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint(`/catalogs/${type}`), opts)
      .pipe(
        map((data: any) => ({ data: data.data.map(( name ) => ({ name }) ) })),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  createLuminary(data, options = { error: true }) {
    let performedPhotos = of(null);

    if (data.photos) {
      performedPhotos = this.preparePhotos(data.photos)
    }
    const opts = {
      headers: new HttpHeaders({ timeout: '10000' })
    };

    return performedPhotos
      .pipe(
        concatMap((photos) =>{
            const formData = getLuminaryFormData(data, photos.filter((photo) => !!photo));

            formData.delete('photos');
            return this.http.post(endpoint('/luminaries/v2'), formData, opts)
                .pipe(
                    tap(() => this.toast.show('Luminaria creada correctamente', 'ok')),
                    catchError((error) => options.error && this.handleError(error))
            )
        })
      );
  }

  listLuminaries(params = {}, options = { error: true, timeout: false }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/luminaries'), opts)
      .pipe(
        options.timeout ? timeout(5000): tap(() => {}),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listPois(params = {}, options = { error: true, timeout: false }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/luminaries/pois'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  lisPublicPois(params = {}, options = { error: true, timeout: false, token: '' }) {
    const performedOptions = {...{ error: true, timeout: false, token: '' }, ...options};
    const opts = {
        params: new HttpParams({fromString: this.getQPFromJson(params)}),
        headers: { 'X-Recaptcha-Token': performedOptions.token },
    };

    return this.http.get(endpoint('/pois'), opts)
      .pipe(
        catchError((error) => performedOptions.error && this.handleError(error))
      );
  }

  removeLuminary(idLuminary, options = { error: true }) {
    return this.http.delete(endpoint(`/luminaries/${idLuminary}`))
      .pipe(
        tap(() => this.toast.show('Luminaria eliminada correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getBulkLuminaries(params = {}, options = { error: true, timeout: false }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/luminaries/bulk'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }
  bulkLuminaries(project, fileToUpload: File, options = { error: true }) {
    const formData: FormData = new FormData();

    formData.append('bulkFile', fileToUpload, fileToUpload.name);
    return this.http.post(endpoint(`/luminaries/bulk/${project}`), formData)
      .pipe(
        tap(() => this.toast.show('Luminarias volcadas correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
    );
  }

  createAsocMaterial(data, options = { error: true }) {
    return this.http.post(endpoint('/asociate-materials'), data)
      .pipe(
        tap(() => this.toast.show('Material asociado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listAsocMaterial(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/asociate-materials'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getAsocMaterial(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/asociate-materials/materials'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  changeAsocMaterial(id, params = {}, options = { error: true }) {
    return this.http.patch(endpoint(`/asociate-materials/${id}`), params)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  updateRepairmentData(id, params = {}, options = { error: true }) {
    return this.http.patch(endpoint(`/maintenances/repairment-data/${id}`), params)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  removeAsocMaterials(id, options = { error: true }) {
    return this.http.delete(endpoint(`/asociate-materials/${id}`))
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  deleteMaterial(id, options = { error: true }) {
    return this.http.delete(endpoint(`/materials/${id}`))
      .pipe(
        tap(() => this.toast.show('Material eliminado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getLuminaryDetail(id, params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint(`/luminaries/${id}`), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  createMaintenance(data, options = { error: true }) {
    return this.http.post(endpoint('/maintenances'), data, { headers: new HttpHeaders({  timeout: '10000' })})
      .pipe(
        tap(() => this.toast.show('Mantenimiento creado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listMaintenances(params = { }, options = { error: true, cache: false }) {
    const opts = {
      params: new HttpParams({ fromString: this.getQPFromJson(params) }),
      headers: new HttpHeaders({ 'cache': `${options.cache}`, timeout: '6000' })
    };

    return this.http.get(endpoint('/maintenances'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listMaintenancesV2(params = { }, options = { error: true, cache: false }) {
    const opts = {
      params: new HttpParams({ fromString: this.getQPFromJson(params) }),
      headers: new HttpHeaders({ 'cache': `${options.cache}`, timeout: '6000' })
    };

    return this.http.get(endpoint('/maintenances/v2'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getProjectParticipants(role, params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint(`/projects/participants/${role}/`), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  assignMaintenancesToTechnical(maintenances, options = { error: true }) {
    return this.http.patch(endpoint('/maintenances/assign-to-technician/'), maintenances)
      .pipe(
        tap(() => this.toast.show('Mantenimiento asignado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }


  assignExpansionsToTechnical(expansions, options = { error: true }) {
    return this.http.patch(endpoint('/expansions/assign-to-technician/'), expansions)
      .pipe(
        tap(() => this.toast.show('Expansion asignado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  assignModernizationsToTechnical(modernizations, options = { error: true }) {
    return this.http.patch(endpoint('/modernizations/assign-to-technician/'), modernizations)
      .pipe(
        tap(() => this.toast.show('Modernizacion asignada correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  assignMaterials(idUser, data, options = { error: true }) {
    return this.http.post(endpoint(`/users/${idUser}/assignMaterials`), data)
      .pipe(
        tap(() => this.toast.show('Material asignado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  savePreassignedMaterials(idUser, data, options = { error: true }) {
    return this.http.patch(endpoint(`/users/${idUser}/preassignedMaterials`), data)
      .pipe(
        tap(() => this.toast.show('Material preasignado actualizado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  returnToStore(idUser, data, options = { error: true }) {
    return this.http.patch(endpoint(`/users/${idUser}/returnMaterialsToStore`), data)
      .pipe(
        tap(() => this.toast.show('Material devuelto correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  returnToInventory(idUser, data, options = { error: true }) {
    return this.http.patch(endpoint(`/users/${idUser}/returnMaterialsToInventory`), data)
      .pipe(
        tap(() => this.toast.show('Material devuelto a inventario correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  confirmMaterial(idUser, data = {}, options = { error: true }) {
    return this.http.patch(endpoint(`/users/${idUser}/confirmMaterial`), data)
      .pipe(
        tap(() => this.toast.show('Material asignado confirmado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  removePreassignedMaterials(idUser, options = { error: true }) {
    return this.http.delete(endpoint(`/users/${idUser}/preassignedMaterials`))
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  changeMaintenanceState(idMaintenance, { state, answers, questions }, options = { error: true }) {
    return this.http.patch(endpoint(`/maintenances/${idMaintenance}/changeState`), { state, answers, questions })
      .pipe(
        timeout(5000),
        tap(() => this.toast.show('Mantenimiento cambiado de estado', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getDetailMaintenance(idMaintenance, options = { error: true }) {
    return this.http.get(endpoint(`/maintenances/detail/${idMaintenance}`))
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  postponeMaintenance(idMaintenance, data, options = { error: true }) {
    return this.http.post(endpoint(`/maintenances/${idMaintenance}/postpone`), data)
      .pipe(
        timeout(5000),
        tap(() => this.toast.show('Mantenimiento postpueto correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  undoMaterials(idMaintenance, options = { error: true }) {
    return this.http.patch(endpoint(`/maintenances/${idMaintenance}/undoMaterials`), {})
      .pipe(
        timeout(5000),
        tap(() => this.toast.show('Materiales eliminados correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  fetchStats(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/maintenances/stats'), opts)
      .pipe(
        timeout(5000),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getFile(fileName, params = {}, options = { error: true, contentType: undefined }) {
    const opts = {
      params: new HttpParams({fromString: this.getQPFromJson(params)}),
      headers: new HttpHeaders({
        'Content-Type': options.contentType
      })
    };

    return this.http.get(endpoint(`/${fileName}`), {responseType: 'blob', ...opts})
      .pipe(
        timeout(7000),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  enrollmentFingerprint(publicKey, options = { error: true }): Observable<any> {
    return this.http.post(endpoint('/fingerprint'), { publicKey }, { observe: 'response' })
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      )
  }

  signFile(file, id, digest, options = { error: true }) {
    return this.http.post(endpoint('/documents/sign'), { fileName: file, taskId: id, digest })
      .pipe(
        timeout(10000),
        tap(() => this.toast.show('Documentación firmada correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  recoverAccess(data, options = { error: true }): Observable<any> {
    return this.http.post(endpoint('/recover-access'), data)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      )
  }

  removeMaintenance(id, options = { error: true }) {
    return this.http.delete(endpoint(`/maintenances/${id}`))
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  removeExpansion(id, options = { error: true }) {
    return this.http.delete(endpoint(`/expansions/${id}`))
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  removeModernization(id, options = { error: true }) {
    return this.http.delete(endpoint(`/modernizations/${id}`))
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  createPQR(data, options = { error: true }) {
    return this.http.post(endpoint('/pqr'), data)
      .pipe(
        tap(() => this.toast.show('PQR creado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }


  updateMaintenance(idMaintenance, {
    questions = null,
    answers = null,
    height = null,
    ascendMethod = null,
    luminary = null,
    photos = null,
    state = null,
    usedMaterials = null,
    extractedMaterials = null,
    description = null,
    repairmentDate = null,
    openDate = null,
    activities = null,
    newLuminaryNumber = null,
    luminaryOfTemporalUse = null,
    newTechnology = null,
    newPower = null,
    allegedCauseOfFail = null,
  }, options = { error: true, alerts: true, timeout: true }) {
    const opts = {
      headers: new HttpHeaders({ ... options.timeout && { timeout: '10000' } })
    };
    const data = {
      newTechnology,
      newPower,
      newLuminaryNumber,
      luminaryOfTemporalUse,
      questions,
      answers,
      openDate,
      ascendMethod,
      height,
      luminary,
      photos,
      state,
      usedMaterials,
      extractedMaterials,
      description,
      repairmentDate,
      activities,
      allegedCauseOfFail,
    };
    return this.http.patch(
      endpoint(`/maintenances/${idMaintenance}`),
      data,
      opts
    ).pipe(
      tap(() => options.alerts && this.toast.show('Mantenimiento actualizado correctamente', 'ok')),
      catchError((error) => options.error && this.handleError(error))
    );
  }

  preparePhotos(photos) {
    return from(Promise.all(photos.map((photo) => {
      return new Promise((resolve) => {
        if (!photo.includes('/base64/')) {
          resolve(undefined);

          return;
        }
        this.cacheService.getBlob(photo, !this.native.isNative).then((photo64) => {
          resolve(photo64);
        });
      });
    })));
  }

  updateLuminary(idLuminary, data, options = { error: true }) {
    let performedPhotos = of(null);
    
    if (data.photos) {
        performedPhotos = this.preparePhotos(data.photos)
    }
    const opts = {
        headers: new HttpHeaders({ timeout: '10000' })
    };
    
    return performedPhotos
      .pipe(
        concatMap((photos) =>{
            const formData: FormData = getLuminaryFormData(data, photos.filter((photo) => !!photo));

            return this.http.patch(endpoint(`/luminaries/v2/${idLuminary}`), formData, opts)
                .pipe(
                    tap(() => this.toast.show('Luminaria actualizada correctamente', 'ok')),
                    catchError((error) => options.error && this.handleError(error))
                )
            })
      );
  }

  getProjectDetail(id, params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint(`/projects/${id}`), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  updateProject(id, data, options = { error: true }) {
    return this.http.patch(endpoint(`/projects/${id}`), data)
      .pipe(
        tap(() => this.toast.show('Proyecto guardado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getDailyQuestionary(id, params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint(`/projects/${id}/questionaries`), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getTaskDetail(id, options = { error: true }) {
    return this.http.get(endpoint(`/tasks/${id}`))
      .pipe(
        timeout(5000),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getTasks(params, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint(`/tasks/list`), opts)
      .pipe(
        timeout(5000),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getStoreDetail(id, params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint(`/stores/${id}`), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getStoresWithStock(params, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint(`/stores/list-with-stock`), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  setCoworker(idUser, params, options = { error: true }) {
    return this.http.patch(endpoint(`/users/${idUser}/coworkers`), params)
      .pipe(
        tap(() => this.toast.show('Grupo guardado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }


  updateDailyQuestionary(id, params, options = { error: true }) {
    return this.http.patch(endpoint(`/projects/${id}/questionaries`), params)
      .pipe(
        timeout(4000),
        tap(() => this.toast.show('Cuestionario añadido correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  performDocumentation(params, options = { error: true }): Observable<any> {
    return this.http.post(endpoint('/documents'), params)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      )
  }

  createStore(data, options = { error: true }) {
    return this.http.post(endpoint('/stores'), data)
      .pipe(
        tap(() => this.toast.show('Almacén creado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  addToStore(storeId, items, options = { error: true }) {
    return this.http.patch(endpoint(`/stores/${storeId}/items`), items)
      .pipe(
        tap(() => this.toast.show('Items añadidos al almacén', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  signinWithRecoverToken(data, options = { error: true }) {
    return this.http.post(endpoint('/session/recover-access'), data, { observe: 'response' })
    .pipe(
      map(data => ({
        user: data.body,
        token: data.headers.get('jwt'),
        tokenNative: data.headers.get('jwt-native')
      })),
      catchError((error) => options.error && this.handleError(error))
    )
  }

  createExpansion(data, options = { error: true }) {
    return this.http.post(endpoint('/expansions'), data)
      .pipe(
        timeout(5000),
        tap(() => this.toast.show('Expansion creada correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  updateExpansion(id, data, options = { error: true }) {
    return this.http.patch(endpoint(`/expansions/${id}`), data)
      .pipe(
        tap(() => this.toast.show('Expansión creada correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  updateModernization(id, data, options = { error: true }) {
    return this.http.patch(endpoint(`/modernizations/${id}`), data)
      .pipe(
        tap(() => this.toast.show('Modernización guardada correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  createModernization(data, options = { error: true }) {
    return this.http.post(endpoint('/modernizations'), data)
      .pipe(
        timeout(15000),
        tap(() => this.toast.show('Modernización creada correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getEvidence(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/evidences'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listByTechnologyAndPower(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/luminaries/group-by-technology-and-power'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }


  closePQR(id, data, options = { error: true }) {
    return this.http.patch(endpoint(`/pqr/${id}`), data)
      .pipe(
        tap(() => this.toast.show('PQR cerrado correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }


  listManualReports(params = {}, options = { error: true, timeout: false }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/reports/manual-report'), opts)
      .pipe(
        options.timeout ? timeout(5000): tap(() => {}),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  addReport(data, options = { error: true }) {
    return this.http.post(endpoint(`/reports/manual-report`), data)
      .pipe(
        tap(() => this.toast.show('Reporte añadido correctamente', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getVersion() {
    return this.http.get(endpoint('/version'))
      .pipe(
        catchError((error) => of({}))
      );
  }

  private handleError(error: any) {
    const data = {
      status: error.status,
      error: error.error
    };
    if (error.name === 'TimeoutError') {
      data.status = 0;
      data.error = {
        code: -2,
        message: 'Conexión limitada'
      }
    } else if (error.status === 0) {
      data.status = 0;
      data.error = {
        code: -1,
        message: 'No dispones de conexión'
      }
    }

    error.code =  data.error.code;
    // error.message = data.error.message;
    
    switch(data.status) {
      case 0:
        this.toast.show(data.error.message);
        break;
      case 401:
        error.code = -3;
        if (SessionStore.isExpiredSession() && error.method === 'GET') {
            return throwError(error);
        } else {
            SessionStore.expire();
        }
        break;
      // Perder sesión
      case 402:
        if (data.error?.code === 666) {
          this.toast.show(data.error.message);
          return;
        }
        SessionStore.expire();
        this.toast.show(data.error.message);
        break;
      case 422:
        this.toast.show(data.error.message);
      break;
      default:
        this.toast.show(data.error.message || 'Servicio temporalmente no disponible');
    }
    return throwError(error);
  };

  private getQPFromJson(param) {
    return Object.keys(param).map((key) => {
      if (key && Array.isArray(param[key])) {
        return param[key].reduce((accumulator, name) => accumulator.append(key, name), new HttpParams());
      }
      return key && `${key}=${param[key]}`;
    }).filter(key=>key).join('&');
  }

  listPQRS(params = {}, options = { error: true, timeout: false }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/pqr'), opts)
      .pipe(
        options.timeout ? timeout(5000): tap(() => {}),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listOrders(params = {}, options = { error: true, timeout: false }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/reports/orders'), opts)
      .pipe(
        options.timeout ? timeout(5000): tap(() => {}),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listOrdersDetailed(params = {}, options = { error: true, timeout: false }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/reports/orders/detailed'), opts)
      .pipe(
        options.timeout ? timeout(5000): tap(() => {}),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  reportDetailedMaterials(params = {}, options = { error: true, timeout: false }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/materials/reports/detailed'), opts)
      .pipe(
        options.timeout ? timeout(5000): tap(() => {}),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  reportAggregatedMaterials(params = {}, options = { error: true, timeout: false }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/materials/reports/aggregated'), opts)
      .pipe(
        options.timeout ? timeout(5000): tap(() => {}),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  downloadReportAggregatedMaterials(params = {}, options = { error: true, timeout: false }) {
    return this.getFile('materials/reports/aggregated/download', params,  { error: true, contentType: 'text/csv'});
  }

  downloadReportDetailedMaterials(params = {}, options = { error: true, timeout: false }) {
    return this.getFile('materials/reports/detailed/download', params,  { error: true, contentType: 'text/csv'});
  }

  downloadOrders(params = {}, options = { error: true, timeout: false }) {
    return this.getFile('reports/orders/download', params,  { error: true, contentType: 'text/csv'});
  }

  downloadDetailedOrders(params = {}, options = { error: true, timeout: false }) {
    return this.getFile('reports/orders/detailed/download', params,  { error: true, contentType: 'text/csv'});
  }

  downloadReportPQRs(params = {}, options = { error: true, timeout: false }) {
    return this.getFile('pqr/reports/download', params,  { error: true, contentType: 'text/csv'});
  }

  getOrderTask(maintenanceId, params = {}, options = { error: true, contentType: undefined }) {
    return this.getFile(`maintenances/${maintenanceId}/order-task`, params, options);
  }

  downloadPQR(pqrId, params = {}, options = { error: true, contentType: undefined }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.getFile(`pqr/${pqrId}/attention-file`, params, options);
  }

  downloadLuminaries(params = {}, options = { error: true, timeout: false }) {
    return this.getFile('luminaries/download', params, { error: true, contentType: 'text/csv'});
  }

  getExpansions(params = {}, options = { error: true, timeout: false, cache: false }) {
    const opts = {
      params: new HttpParams({fromString: this.getQPFromJson(params)}),
      headers: new HttpHeaders({ 'cache': `${options.cache}`, ...options.timeout && { timeout: '5000' } })
    };

    return this.http.get(endpoint('/expansions'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getModernizations(params = {}, options = { error: true, timeout: false, cache: false }) {
    const opts = {
      params: new HttpParams({fromString: this.getQPFromJson(params)}),
      headers: new HttpHeaders({ 'cache': `${options.cache}`, ... options.timeout && { timeout: '5000' } })
    };

    return this.http.get(endpoint('/modernizations'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getPqrDetail(id, params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint(`/pqr/${id}`), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getHistoryLuminary(id, params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint(`/luminaries/${id}/history`), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getModernizationDetail(id, options = { error: true }) {
    return this.http.get(endpoint(`/modernizations/${id}`))
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getExpansionDetail(id, options = { error: true }) {
    return this.http.get(endpoint(`/expansions/${id}`))
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  postIssueData(data, options = { error: true }) {
    this.showStack = true;
    return this.http.post(endpoint('/support'), data )
      .pipe(
        tap(() => this.toast.show('Gracias, contactactaremos con usted en la menor brevedad posible', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  postContactPqr(data,  options = { error: true, timeout: false, token: '' }) {
    const performedOptions = {...{ error: true, timeout: false, token: '' }, ...options};
    const headers = { headers: new HttpHeaders({'X-Recaptcha-Token': performedOptions.token})}

    return this.http.post(endpoint('/contact/pqr'), data, headers )
      .pipe(
        tap(() => this.toast.show('Gracias, su solicitud está en el departamento de triaje.', 'ok')),
        catchError((error) => performedOptions.error && this.handleError(error))
      );
  }

  postContact(data,  options = { error: true, timeout: false, token: '' }) {
    const performedOptions = {...{ error: true, timeout: false, token: '' }, ...options};
    const headers = { headers: new HttpHeaders({'X-Recaptcha-Token': performedOptions.token})}

    return this.http.post(endpoint('/contact'), data, headers )
      .pipe(
        tap(() => this.toast.show('Gracias, su solicitud está en el departamento de triaje.', 'ok')),
        catchError((error) => performedOptions.error && this.handleError(error))
      );
  }

  findCity(params, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/public/cities/'), opts)
      .pipe(
        map((data: any) => ({ data: data.data.map(( name ) => ({ name }) ) })),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  findDepartment(params, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/public/departments/'), opts)
      .pipe(
        map((data: any) => ({ data: data.data.map(( name ) => ({ name }) ) })),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  listUserPqrs(params, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/user-pqrs/'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }

  discardUserPQRS(id, data, options = { error: true }) {
    return this.http.patch(endpoint(`/user-pqrs/reject/${id}`), data)
      .pipe(
        tap(() => this.toast.show('Incidencia descartada', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  getAdminBills(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/admin/bills'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }


   getBills(params = {}, options = { error: true }) {
    const opts = { params: new HttpParams({fromString: this.getQPFromJson(params)}) };

    return this.http.get(endpoint('/bills'), opts)
      .pipe(
        catchError((error) => options.error && this.handleError(error))
      );
  }


  modifyBill(id, data, options = { error: true }) {
    return this.http.patch(endpoint(`/admin/bills/${id}`), data)
      .pipe(
        tap(() => this.toast.show('Factura modificada', 'ok')),
        catchError((error) => options.error && this.handleError(error))
      );
  }

  log(data) {
    return this.http.put(endpoint('/support/log'), { data });
  }

  acceptPrivacyPolicy(userId, data, options = { error: true }) {
    return this.http.patch(endpoint(`/users/${userId}/consent-privacy-policy`), data)
      .pipe(
        catchError(() => {
            return of({})
        })
      );
  }

}
